import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, isRef as _isRef, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col gap-3 w-full animate-slideFadeIn"
}
const _hoisted_2 = {
  key: 0,
  class: "text-xs text-red-600 bg-red-100 px-3 py-2 rounded-lg w-full"
}
const _hoisted_3 = ["disabled"]
const _hoisted_4 = {
  key: 1,
  class: "flex flex-col items-center justify-center gap-3"
}
const _hoisted_5 = { class: "flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-purple-100/50 to-purple-100/10 text-purple-500/80 shadow-inner border border-purple-100" }
const _hoisted_6 = { class: "flex flex-col items-center justify-center gap-2 pt-3" }
const _hoisted_7 = { class: "text-sm text-gray-500 text-center leading-relaxed" }
const _hoisted_8 = { class: "font-semibold text-gray-800" }

import { ref, computed, watch } from "vue"
import FormInput from "./FormInput.vue"
import * as Yup from "yup"
import { useField, useForm } from "vee-validate"
import axios from "@/services/api"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"
import { Check, LoaderCircle } from "lucide-vue-next"
import router from "@/router"


export default /*@__PURE__*/_defineComponent({
  __name: 'ForgetPasswordForm',
  setup(__props) {

const { t } = useI18n()
const store = useStore()
const selectedLanguage = computed(() => store.state.stepper.selectedLanguage)

const isLoading = ref(false)
const errorMessage = ref("")
const isSubmitted = ref(false)
const savedEmail = ref("")

const schema = Yup.object({
    email: Yup.string()
        .required(() => t("logIn.emailRequired"))
        .email(() => t("logIn.emailValidation")),
})

const { handleSubmit } = useForm({
    validationSchema: schema,
})

const { value: email, errorMessage: emailError, meta: emailMeta } = useField<string>("email")

watch(email, () => {
    if (errorMessage.value) {
        errorMessage.value = ""
    }
})

const onSubmit = handleSubmit(async (values) => {
    errorMessage.value = ""
    isLoading.value = true

    try {
        const res = await axios.post("user/requestResetPassword", {
            email: values.email,
            selectedLanguage: selectedLanguage.value,
        })
        if (res.data.message === "This email doesn't exist") {
            isSubmitted.value = false
            isLoading.value = false
            errorMessage.value = res.data.message || "This email doesn't exist"
            return
        } else {
            isSubmitted.value = true

        }
        savedEmail.value = values.email
    } catch (e: any) {

        const res = e.response;
        res.data.messages.forEach(
            (msg: { code: number; message: string; metas: any }) => {
                if (msg.code === 103) {
                    errorMessage.value = msg.message;
                } else if (msg.code === 104) {
                    errorMessage.value = msg.message;
                } else if (msg.code === 105) {
                    errorMessage.value = msg.message;
                } else if (msg.code === 107) {
                    errorMessage.value = msg.message;
                }
                else errorMessage.value = "An error occurred. Please try again later.";
            }
        );
    } finally {
        isLoading.value = false
    }
})

return (_ctx: any,_cache: any) => {
  return (!isSubmitted.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _cache[4] || (_cache[4] = _createElementVNode("div", { class: "flex flex-col gap-2 pb-12" }, [
          _createElementVNode("h2", { class: "text-3xl font-semibold text-gray-600 text-center" }, " Reset Password "),
          _createElementVNode("p", { class: "text-sm text-gray-500 text-center" }, " Enter your email and we’ll send you a reset link. ")
        ], -1)),
        _createVNode(FormInput, {
          modelValue: _unref(email),
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(email) ? (email).value = $event : null)),
          type: "email",
          placeholder: "Enter your email address",
          error: _unref(emailMeta).touched ? _unref(emailError) : ''
        }, null, 8, ["modelValue", "error"]),
        (errorMessage.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(errorMessage.value), 1))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          disabled: isLoading.value,
          onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(onSubmit) && _unref(onSubmit)(...args))),
          class: "w-full mt-3 text-lg bg-gradient-to-r from-purple-500 to-pink-400 text-white font-semibold py-2.5 rounded-xl flex flex-row items-center justify-center gap-2 hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
        }, [
          (isLoading.value)
            ? (_openBlock(), _createBlock(_unref(LoaderCircle), {
                key: 0,
                class: "w-4 h-4 animate-spin"
              }))
            : _createCommentVNode("", true),
          _createTextVNode(" " + _toDisplayString(isLoading.value ? "Sending..." : "Send Reset Link"), 1)
        ], 8, _hoisted_3),
        _createElementVNode("button", {
          class: "text-sm text-gray-500 hover:underline text-center mt-2",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(router).push('/auth/signin')))
        }, " ← Back to Sign In ")
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_unref(Check), { class: "w-5 h-5" })
        ]),
        _cache[8] || (_cache[8] = _createElementVNode("h2", { class: "text-xl font-bold text-gray-600 text-center" }, " Check your inbox ", -1)),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("p", _hoisted_7, [
            _cache[5] || (_cache[5] = _createTextVNode(" A password reset link has been sent to ", -1)),
            _createElementVNode("span", _hoisted_8, _toDisplayString(savedEmail.value ||
                        'your email address'), 1),
            _cache[6] || (_cache[6] = _createTextVNode(". ", -1))
          ]),
          _cache[7] || (_cache[7] = _createElementVNode("p", { class: "text-xs text-gray-400 text-center" }, " Didn’t receive the email? Check your spam folder or try again. ", -1))
        ]),
        _createElementVNode("button", {
          class: "text-sm text-gray-500 hover:underline text-center mt-2",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(router).push('/auth/signin')))
        }, " ← Back to Sign In ")
      ]))
}
}

})